import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { Dance } from '@pirouette/shared';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';

interface SortableLineupItemProps {
  id: string; // dance_id
  dance: Dance;
  sequenceIndex: number;
  computedStartTime: string | null;
  hasConflict: boolean;
  castCount: number;
  onRemove: (danceId: string) => void;
  onOpenCast: (danceId: string) => void;
}

export function SortableLineupItem({
  id,
  dance,
  sequenceIndex,
  computedStartTime,
  hasConflict,
  castCount,
  onRemove,
  onOpenCast,
}: SortableLineupItemProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });

  return (
    <li
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
      }}
      className={`surface flex items-center gap-3 p-3 ${
        hasConflict ? 'ring-1 ring-[var(--color-warn)]' : ''
      }`}
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="cursor-grab touch-none px-2 py-1 text-[var(--color-ink-muted)] hover:text-[var(--color-ink)] focus-visible:outline-none"
        aria-label={`Drag to reorder ${dance.name}`}
        aria-describedby="dnd-kb-hint"
        title="Drag to reorder"
      >
        ⋮⋮
      </button>

      <span className="w-8 text-center font-[var(--font-display)] text-[var(--color-ink-muted)] text-[var(--text-lg)] tabular-nums">
        {sequenceIndex}
      </span>

      <div className="min-w-0 flex-1">
        <p className="truncate font-medium">{dance.name}</p>
        <p className="text-[var(--color-ink-muted)] text-[var(--text-xs)]">
          {dance.style} · {Math.floor(dance.length_seconds / 60)}:
          {String(dance.length_seconds % 60).padStart(2, '0')}
          {computedStartTime && (
            <>
              {' · '}
              {new Date(computedStartTime).toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit',
              })}
            </>
          )}
        </p>
      </div>

      <Badge tone={castCount > 0 ? 'accent' : 'neutral'}>
        {castCount} {castCount === 1 ? 'dancer' : 'dancers'}
      </Badge>

      <Button size="sm" variant="ghost" onClick={() => onOpenCast(dance.id)}>
        Cast
      </Button>
      <Button size="sm" variant="ghost" onClick={() => onRemove(dance.id)}>
        Remove
      </Button>
    </li>
  );
}
